import { useState } from 'react';
import { motion, AnimatePresence, type PanInfo } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface ComicPage {
  src: string;
  alt: string;
  label: string;
}

interface ComicViewerProps {
  pages: ComicPage[];
}

export function ComicViewer({ pages }: ComicViewerProps) {
  const [currentPage, setCurrentPage] = useState(0);
  const [direction, setDirection] = useState(0);

  const paginate = (newDirection: number) => {
    setDirection(newDirection);
    setCurrentPage((prev) => {
      const next = prev + newDirection;
      if (next < 0) return pages.length - 1;
      if (next >= pages.length) return 0;
      return next;
    });
  };

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset: number, velocity: number) => {
    return Math.abs(offset) * velocity;
  };

  return (
    <div className="comic-viewer">
      {/* Desktop: Book Layout (side-by-side) */}
      <div className="hidden md:block">
        <div className="flex justify-center items-stretch gap-1 max-w-5xl mx-auto">
          {pages.map((page, idx) => (
            <div
              key={idx}
              className="relative flex-1 group cursor-pointer"
            >
              <div className="relative overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)] shadow-lg hover:shadow-xl transition-shadow">
                <img
                  src={page.src}
                  alt={page.alt}
                  className="w-full h-auto object-contain"
                  loading="lazy"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                  <span className="text-white text-sm font-medium">{page.label}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Book spine effect */}
        <div className="flex justify-center mt-4">
          <p className="text-sm text-[var(--color-text-muted)]">
            Click on any page to view full size
          </p>
        </div>
      </div>

      {/* Mobile: Swipeable single page */}
      <div className="md:hidden relative">
        <div className="relative overflow-hidden rounded-lg border border-[var(--color-border)] bg-[var(--color-bg-card)]">
          <AnimatePresence mode="wait" initial={false} custom={direction}>
            <motion.div
              key={currentPage}
              custom={direction}
              initial={{ x: direction > 0 ? 300 : -300, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: direction > 0 ? -300 : 300, opacity: 0 }}
              transition={{
                x: { type: "spring", stiffness: 300, damping: 30 },
                opacity: { duration: 0.2 }
              }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={1}
              onDragEnd={(_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
                const swipe = swipePower(info.offset.x, info.velocity.x);
                if (swipe < -swipeConfidenceThreshold) {
                  paginate(1);
                } else if (swipe > swipeConfidenceThreshold) {
                  paginate(-1);
                }
              }}
              className="w-full"
            >
              <img
                src={pages[currentPage].src}
                alt={pages[currentPage].alt}
                className="w-full h-auto"
                draggable={false}
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                <span className="text-white text-sm font-medium">{pages[currentPage].label}</span>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={() => paginate(-1)}
          className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-[var(--color-bg)]/80 backdrop-blur-sm border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-bg)] hover:border-[var(--color-primary)] transition-colors"
          aria-label="Previous page"
        >
          <ChevronLeft size={20} />
        </button>
        <button
          onClick={() => paginate(1)}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-[var(--color-bg)]/80 backdrop-blur-sm border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-bg)] hover:border-[var(--color-primary)] transition-colors"
          aria-label="Next page"
        >
          <ChevronRight size={20} />
        </button>

        {/* Page Indicators */}
        <div className="flex justify-center gap-2 mt-4">
          {pages.map((_, idx) => (
            <button
              key={idx}
              onClick={() => {
                setDirection(idx > currentPage ? 1 : -1);
                setCurrentPage(idx);
              }}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx === currentPage
                  ? 'bg-[var(--color-primary)]'
                  : 'bg-[var(--color-border)] hover:bg-[var(--color-text-muted)]'
              }`}
              aria-label={`Go to page ${idx + 1}`}
            />
          ))}
        </div>

        {/* Swipe hint */}
        <p className="text-center text-sm text-[var(--color-text-muted)] mt-2">
          Swipe to navigate
        </p>
      </div>
    </div>
  );
}
